// animations/entrance-animations.ts
import gsap from 'gsap';

import { AnimationDynamicSettings, AnimationType } from './animation/animations.model';
import { baseBounce, baseFade } from './common-base-animations';
import { directionToDegree } from './dot-cms-aniamtion.helper';

export const entranceAnimations: Partial<
  Record<AnimationType, (s: AnimationDynamicSettings, el: Element, opts: gsap.TweenVars) => gsap.core.Tween>
> = {
  // Use common base animations
  fade: (s, el, opts) => {
    let x = 0,
      y = 0;
    const dist = s.distance ?? 20;

    switch (s.direction) {
      case 'up':
        y = dist;
        break;
      case 'down':
        y = -dist;
        break;
      case 'left':
        x = dist;
        break;
      case 'right':
        x = -dist;
        break;
    }

    gsap.set(el, { x, y });

    return baseFade(el, {
      ...opts,
      x: 0,
      y: 0,
      opacity: 1,
    });
  },

  flip: (s, el, opts) => {
    const direction = s.direction || 'up';
    let rotationProp = 'rotationX';
    let startAngle = -90;

    switch (direction) {
      case 'up':
        rotationProp = 'rotationX';
        startAngle = 90;
        break;
      case 'down':
        rotationProp = 'rotationX';
        startAngle = -90;
        break;
      case 'left':
        rotationProp = 'rotationY';
        startAngle = -90;
        break;
      case 'right':
        rotationProp = 'rotationY';
        startAngle = 90;
        break;
    }

    return gsap.fromTo(
      el,
      {
        [rotationProp]: startAngle,
        transformOrigin: '50% 50%',
        opacity: 0,
      },
      {
        [rotationProp]: 0,
        opacity: 1,
        ease: opts.ease || 'back.out(1.7)', // gives that bounce finish like Wix
        ...opts,
      }
    );
  },
  flap: (s, el, opts) => {
    const foldAngle = s.fold_angle ?? 45;
    const dir = s.direction ?? 'up';
    let rotationProp = 'rotationX';
    let origin = '50% 0%';

    switch (dir) {
      case 'up':
        rotationProp = 'rotationX';
        origin = '50% 0%';
        break;
      case 'down':
        rotationProp = 'rotationX';
        origin = '50% 100%';
        break;
      case 'left':
        rotationProp = 'rotationY';
        origin = '0% 50%';
        break;
      case 'right':
        rotationProp = 'rotationY';
        origin = '100% 50%';
        break;
    }

    return gsap.fromTo(
      el,
      {
        [rotationProp]: foldAngle,
        opacity: 0,
        transformOrigin: origin,
      },
      {
        [rotationProp]: 0,
        opacity: 1,
        ease: opts.ease ?? 'power1.out',
        ...opts,
      }
    );
  },
  bounce: (_s, el, opts) => baseBounce(el, opts),
  expand: (s, el, opts) => {
    let origin = 'center center';
    switch (s.direction) {
      case 'left': {
        origin = 'left center';
        break;
      }
      case 'right': {
        origin = 'right center';
        break;
      }
      case 'up': {
        origin = 'center top';
        break;
      }
      case 'down':
        {
          origin = 'center bottom';
          // No default
        }
        break;
    }

    return gsap.fromTo(
      el,
      { scale: 0, opacity: 0, transformOrigin: origin },
      { scale: opts.scale ?? 1, opacity: 1, ease: opts.ease ?? 'power1.out', yoyo: false, ...opts }
    );
  },
  // Entrance-specific animations
  glide: (s, el, opts) => {
    const safe = <T>(val: T | undefined, def: T): T => (val !== undefined ? val : def);
    const angle = directionToDegree(s.direction ?? 'up');
    const dist = safe(s.distance, 100);

    return gsap.fromTo(
      el,
      {
        x: -Math.cos(angle) * dist,
        y: -Math.sin(angle) * dist,
        opacity: safe(s.startOpacity, 0),
      },
      {
        x: 0,
        y: 0,
        opacity: 1,
        duration: safe(s.duration, 1),
        delay: safe(s.delay, 0),
        ease: safe(s.easing, 'power1.out'),
        ...opts,
      }
    );
  },

  reveal: (s, el, opts) => {
    let clipFrom;
    switch (s.direction) {
      case 'left':
        clipFrom = 'inset(0 100% 0 0)';
        break;
      case 'right':
        clipFrom = 'inset(0 0 0 100%)';
        break;
      case 'up':
        clipFrom = 'inset(100% 0 0 0)';
        break;
      case 'down':
        clipFrom = 'inset(0 0 100% 0)';
        break;
      case 'center':
      default:
        clipFrom = 'inset(100% 0 0 0)';
    }
    return gsap.from(el, {
      ...opts,
      clipPath: clipFrom,
      opacity: 1,
    });
  },

  shutters: (s, el, opts) => gsap.from(el, { opacity: 0, stagger: s.stagger ?? 0.1, ...opts }),

  slide: (s, el, opts) => {
    let x = 0,
      y = 0;
    const dist = s.distance ?? 100;
    const dir = s.direction ?? 'up';

    switch (dir) {
      case 'left': {
        x = dist;
        break;
      }
      case 'right': {
        x = -dist;
        break;
      }
      case 'up': {
        y = dist;
        break;
      }
      case 'down':
        {
          y = -dist;
          // No default
        }
        break;
    }

    return gsap.from(el, {
      ...opts,
      x,
      y,
      opacity: 0,
    });
  },

  blur: (s, el, opts) =>
    gsap.fromTo(el, { filter: `blur(${s.blur_intensity ?? 5}px)` }, { filter: 'blur(0px)', ...opts, opacity: 1 }),

  spin: (s, el, opts) =>
    gsap.to(el, {
      rotation: s.direction === 'counter-clockwise' ? -360 * (s.spin_count ?? 1) : 360 * (s.spin_count ?? 1),
      ...opts,
    }),

  turn: (s, el, opts) => {
    const direction = s.direction || 'up';
    let rotationProp = 'rotationX';
    let startAngle = 180;

    switch (direction) {
      case 'left':
        rotationProp = 'rotationY';
        startAngle = -180;
        break;
      case 'right':
        rotationProp = 'rotationY';
        startAngle = 180;
        break;
      case 'up':
        rotationProp = 'rotationX';
        startAngle = 180;
        break;
      case 'down':
        rotationProp = 'rotationX';
        startAngle = -180;
        break;
    }

    return gsap.fromTo(
      el,
      {
        [rotationProp]: startAngle,
        opacity: 0,
        transformOrigin: '50% 50%',
      },
      {
        [rotationProp]: 0,
        opacity: 1,
        ...opts,
      }
    );
  },

  arc: (s, el, opts) => {
    const direction = s.direction || 'left';
    const distance = 90;
    let x = 0,
      y = 0,
      rotation = 0;

    switch (direction) {
      case 'left':
        x = -distance;
        y = -distance * 0.5;
        rotation = -45;
        break;
      case 'right':
        x = distance;
        y = -distance * 0.5;
        rotation = 45;
        break;
      case 'up':
        y = -distance;
        x = distance * 0.5;
        rotation = 45;
        break;
      case 'down':
        y = distance;
        x = distance * 0.5;
        rotation = -45;
        break;
    }

    return gsap.fromTo(
      el,
      {
        x,
        y,
        rotation,
        opacity: 0,
        transformOrigin: '50% 50%',
      },
      {
        x: 0,
        y: 0,
        rotation: 0,
        opacity: 1,
        ...opts,
      }
    );
  },

  grow: (s, el, opts) => {
    const dist = s.distance ?? 100;
    const dir = s.direction ?? 'up';
    let x = 0,
      y = 0;

    switch (dir) {
      case 'left':
        x = -dist;
        break;
      case 'right':
        x = dist;
        break;
      case 'up':
        y = -dist;
        break;
      case 'down':
        y = dist;
        break;
    }

    return gsap.fromTo(
      el,
      { opacity: 0, scale: 0 },
      {
        ...opts,
        x,
        y,
        scale: s.scale ?? 1,
        opacity: 1,
      }
    );
  },

  shrink: (s, el, opts) =>
    gsap.fromTo(
      el,
      {
        scale: 2,
        opacity: 0,
        transformOrigin: '50% 50%',
      },
      {
        scale: s.scale ?? 1,
        opacity: 1,
        ...opts,
      }
    ),

  wink: (s, el, opts) => {
    const isH = s.orientation === 'horizontal';
    return gsap.from(el, {
      ...opts,
      scaleX: isH ? 0.1 : 1,
      scaleY: isH ? 1 : 0.1,
      opacity: 0,
      transformOrigin: isH ? 'left center' : 'center top',
    });
  },
  shape: (s, el, opts) => {
    let fromClip,
      toClip = 'inset(0% 0% 0% 0%)';
    switch (s.shape) {
      case 'circle':
        fromClip = 'circle(0% at 50% 50%)';
        toClip = 'circle(75% at 50% 50%)';
        break;
      case 'oval':
        fromClip = 'ellipse(0% 0% at 50% 50%)';
        toClip = 'ellipse(75% 75% at 50% 50%)';
        break;
      case 'diamond':
        fromClip = 'polygon(50% 48%, 52% 50%, 50% 52%, 48% 50%)';
        toClip = 'polygon(50% 2%, 98% 50%, 50% 98%, 2% 50%)';
        break;
      case 'square':
      default:
        fromClip = 'inset(100% 100% 100% 100%)';
        toClip = 'inset(0% 0% 0% 0%)';
    }
    return gsap.fromTo(el, { clipPath: fromClip, opacity: 0 }, { ...opts, clipPath: toClip, opacity: 1, yoyo: false });
  },

  tilt: (s, el, opts) => {
    const direction = s.direction || 'left'; // "left" | "right" | "up" | "down"
    let rotationX = 0,
      rotationY = 0,
      x = 0,
      y = 0;

    switch (direction) {
      case 'left':
        rotationY = -15;
        x = -40;
        break;
      case 'right':
        rotationY = 15;
        x = 40;
        break;
      case 'up':
        rotationX = 15;
        y = -40;
        break;
      case 'down':
        rotationX = -15;
        y = 40;
        break;
    }

    return gsap.fromTo(
      el,
      {
        rotationX,
        rotationY,
        x,
        y,
        opacity: 0,
        transformOrigin: '50% 50%',
      },
      {
        rotationX: 0,
        rotationY: 0,
        x: 0,
        y: 0,
        opacity: 1,
        ...opts,
      }
    );
  },

  float: (s, el, opts) => {
    const dir = s.direction || 'up';
    const y = dir === 'up' ? 20 : dir === 'down' ? -20 : 0;
    const x = dir === 'left' ? 20 : dir === 'right' ? -20 : 0;

    return gsap.from(el, {
      ...opts,
      x,
      y,
      opacity: 0,
    });
  },
};
